/**
 * Centralized utility for job type map pin icons
 * These are the consistent icons used across:
 * - Marketplace map pins
 * - Wizard type selectors
 * - Detail modals
 * - Listing cards
 * 
 * Landing page uses Lucide icons directly (not these SVGs)
 */

// Circle-pin SVGs (for UI elements: cards, wizard, modals)
import jobPostMapPin from '@/assets/job-post-map-pin.svg';
import jobContractMapPin from '@/assets/job-contract-map-pin.svg';
import jobProfileMapPin from '@/assets/job-profile-map-pin.svg';
import jobSeekingMapPin from '@/assets/job-seeking-map-pin.svg';

// Icon-only SVGs (for Google Map markers - no circle background)
import jobPostMarkerIcon from '@/assets/job-post-marker-icon.svg';
import jobContractMarkerIcon from '@/assets/job-contract-marker-icon.svg';
import jobProfileMarkerIcon from '@/assets/job-profile-marker-icon.svg';
import jobSeekingMarkerIcon from '@/assets/job-seeking-marker-icon.svg';

export type JobWizardType = 'job_post' | 'job_contract' | 'job_profile' | 'job_seeking';

// Map pin SVG icons for each job type (with white circle - for UI elements)
export const JOB_TYPE_MAP_PINS: Record<JobWizardType, string> = {
  job_post: jobPostMapPin,
  job_contract: jobContractMapPin,
  job_profile: jobProfileMapPin,
  job_seeking: jobSeekingMapPin,
};

// Icon-only SVGs for each job type (no circle - for Google Maps markers)
export const JOB_TYPE_MARKER_ICONS: Record<JobWizardType, string> = {
  job_post: jobPostMarkerIcon,
  job_contract: jobContractMarkerIcon,
  job_profile: jobProfileMarkerIcon,
  job_seeking: jobSeekingMarkerIcon,
};

/**
 * Get the map pin icon for a specific job listing type (with circle background)
 * Use this for UI elements: cards, wizard selector, modal headers
 * @param listingType - The job listing type (job_post, job_contract, job_profile, job_seeking)
 * @returns The SVG icon path for the job type
 */
export const getJobTypeMapPin = (listingType?: string): string => {
  if (!listingType) return jobPostMapPin;
  
  switch (listingType) {
    case 'job_post': return jobPostMapPin;
    case 'job_contract': return jobContractMapPin;
    case 'job_profile': return jobProfileMapPin;
    case 'job_seeking': return jobSeekingMapPin;
    default: return jobPostMapPin;
  }
};

/**
 * Get the marker icon for a specific job listing type (icon only, no circle)
 * Use this for Google Maps markers where createIconWithBackground adds the circle
 * @param listingType - The job listing type (job_post, job_contract, job_profile, job_seeking)
 * @returns The SVG icon path for the job type
 */
export const getJobTypeMarkerIcon = (listingType?: string): string => {
  if (!listingType) return jobPostMarkerIcon;
  
  switch (listingType) {
    case 'job_post': return jobPostMarkerIcon;
    case 'job_contract': return jobContractMarkerIcon;
    case 'job_profile': return jobProfileMarkerIcon;
    case 'job_seeking': return jobSeekingMarkerIcon;
    default: return jobPostMarkerIcon;
  }
};

/**
 * Configuration for job type display including labels and colors
 */
export const JOB_TYPE_CONFIG: Record<JobWizardType, {
  label: string;
  shortLabel: string;
  isOffering: boolean;
  color: string;
  bgColor: string;
}> = {
  job_post: {
    label: 'Job Posting',
    shortLabel: 'Post',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  job_contract: {
    label: 'Contract Opportunity',
    shortLabel: 'Contract',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  job_profile: {
    label: 'Professional Profile',
    shortLabel: 'Profile',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  job_seeking: {
    label: 'Actively Seeking',
    shortLabel: 'Seeking',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
};

/**
 * Check if a job listing type is an offering (company posting)
 */
export const isJobOffering = (listingType?: string): boolean => {
  return listingType === 'job_post' || listingType === 'job_contract';
};

/**
 * Check if a job listing type is a profile (professional seeking)
 */
export const isJobProfile = (listingType?: string): boolean => {
  return listingType === 'job_profile' || listingType === 'job_seeking';
};
